/**
 * SEO Defaults Utility
 * 
 * Centralized SEO default values and helpers for consistent SEO across the application.
 * This ensures fallback values when specific SEO data is not available.
 */

import { useSEOSettings } from '@/contexts/AppSettingsContext';
import { useBrandingSettings } from '@/contexts/AppSettingsContext';

import { resolveSchemaBaseUrl } from '@webready/shared';

/**
 * Get the base URL for canonical links (runtime origin in browser).
 */
export const getBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // SSR fallback - should be configured via environment variable
  return import.meta.env.VITE_APP_URL || 'https://example.com';
};

/**
 * Origin for JSON-LD @id fragments — prefers canonical_url from app settings.
 */
export const getSchemaBaseUrl = (options?: {
  canonicalUrl?: string | null;
}): string =>
  resolveSchemaBaseUrl({
    canonicalUrl: options?.canonicalUrl,
    envBaseUrl: import.meta.env.VITE_APP_URL,
    runtimeOrigin: typeof window !== 'undefined' ? window.location.origin : null,
  });

/**
 * Get default SEO values
 * Note: These are fallback values only. Actual values should come from AppSettingsContext.
 */
export const getDefaultSEO = () => {
  // These will be overridden by AppSettingsContext in actual usage
  return {
    siteName: '', // Will be populated from app settings
    siteDescription: '', // Will be populated from app settings
    siteUrl: getBaseUrl(),
    defaultImage: `${getBaseUrl()}/og-default.jpg`,
    defaultKeywords: '', // Will be populated from app settings
    locale: 'ro-RO',
    localeAlternate: 'en_US',
  };
};

/**
 * Generate canonical URL from path
 */
export const getCanonicalUrl = (path: string): string => {
  const baseUrl = getBaseUrl();
  // Ensure path starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Generate structured data for Organization
 */
export const generateOrganizationStructuredData = (settings?: {
  appName?: string;
  companyDescription?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logoUrl?: string;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
  };
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: settings?.appName || defaults.siteName,
    description: settings?.companyDescription || defaults.siteDescription,
    url: baseUrl,
    logo: settings?.logoUrl
      ? settings.logoUrl.startsWith('http')
        ? settings.logoUrl
        : `${baseUrl}${settings.logoUrl}`
      : `${baseUrl}/logo.png`,
    ...(settings?.contactEmail && {
      contactPoint: {
        '@type': 'ContactPoint',
        email: settings.contactEmail,
        contactType: 'customer service',
        ...(settings.contactPhone && { telephone: settings.contactPhone }),
      },
    }),
    ...(settings?.address && {
      address: {
        '@type': 'PostalAddress',
        addressLocality: settings.address,
      },
    }),
    ...(settings?.socialMedia && {
      sameAs: [
        settings.socialMedia.facebook,
        settings.socialMedia.instagram,
        settings.socialMedia.twitter,
        settings.socialMedia.linkedin,
        settings.socialMedia.youtube,
      ].filter(Boolean) as string[],
    }),
  };
};

/**
 * @deprecated Prefer buildWebPageGraphSchema from @webready/shared (unified @graph).
 * Generate structured data for WebPage
 */
export const generateWebPageStructuredData = (options: {
  title: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: options.title,
    description: options.description,
    url: options.url,
    inLanguage: defaults.locale,
    ...(options.image && {
      image: options.image.startsWith('http') ? options.image : `${baseUrl}${options.image}`,
    }),
    ...(options.datePublished && {
      datePublished: options.datePublished,
    }),
    ...(options.dateModified && {
      dateModified: options.dateModified,
    }),
    ...(options.author && {
      author: {
        '@type': 'Person',
        name: options.author,
      },
    }),
    publisher: {
      '@type': 'Organization',
      name: defaults.siteName,
      url: baseUrl,
    },
  };
};

/**
 * @deprecated Prefer buildServiceGraphSchema from @webready/shared (unified @graph).
 * Generate structured data for Service
 */
export const generateServiceStructuredData = (options: {
  name: string;
  description: string;
  url: string;
  image?: string;
  provider?: string;
  areaServed?: string;
  serviceType?: string;
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: options.name,
    description: options.description,
    url: options.url,
    provider: {
      '@type': 'Organization',
      name: options.provider || defaults.siteName,
      url: baseUrl,
    },
    ...(options.image && {
      image: options.image.startsWith('http') ? options.image : `${baseUrl}${options.image}`,
    }),
    ...(options.areaServed && {
      areaServed: {
        '@type': 'Country',
        name: options.areaServed,
      },
    }),
    ...(options.serviceType && {
      serviceType: options.serviceType,
    }),
  };
};

/**
 * Generate structured data for BlogPosting
 */
export const generateBlogPostingStructuredData = (options: {
  title: string;
  description: string;
  url: string;
  image?: string;
  author?: string;
  authorImage?: string;
  datePublished: string;
  dateModified?: string;
  category?: string;
  keywords?: string[];
  readingTime?: number;
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: options.title,
    description: options.description,
    url: options.url,
    inLanguage: defaults.locale,
    ...(options.image && {
      image: options.image.startsWith('http') ? options.image : `${baseUrl}${options.image}`,
    }),
    ...(options.author && {
      author: {
        '@type': 'Person',
        name: options.author,
        ...(options.authorImage && {
          image: options.authorImage.startsWith('http') 
            ? options.authorImage 
            : `${baseUrl}${options.authorImage}`,
        }),
      },
    }),
    datePublished: options.datePublished,
    ...(options.dateModified && {
      dateModified: options.dateModified,
    }),
    publisher: {
      '@type': 'Organization',
      name: defaults.siteName,
      url: baseUrl,
    },
    ...(options.category && {
      articleSection: options.category,
    }),
    ...(options.keywords && options.keywords.length > 0 && {
      keywords: options.keywords.join(', '),
    }),
    ...(options.readingTime && {
      timeRequired: `PT${options.readingTime}M`,
    }),
  };
};

/**
 * Generate structured data for Blog (collection)
 */
export const generateBlogStructuredData = (options: {
  name: string;
  description: string;
  url: string;
  image?: string;
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: options.name,
    description: options.description,
    url: options.url,
    inLanguage: defaults.locale,
    ...(options.image && {
      image: options.image.startsWith('http') ? options.image : `${baseUrl}${options.image}`,
    }),
    publisher: {
      '@type': 'Organization',
      name: defaults.siteName,
      url: baseUrl,
    },
  };
};

/**
 * Generate structured data for ContactPage
 */
export const generateContactPageStructuredData = (options: {
  title: string;
  description: string;
  url: string;
  organizationName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: options.title,
    description: options.description,
    url: options.url,
    inLanguage: defaults.locale,
    ...(options.organizationName && {
      mainEntity: {
        '@type': 'Organization',
        name: options.organizationName,
        ...(options.contactEmail && {
          email: options.contactEmail,
        }),
        ...(options.contactPhone && {
          telephone: options.contactPhone,
        }),
        ...(options.address && {
          address: {
            '@type': 'PostalAddress',
            addressLocality: options.address,
          },
        }),
      },
    }),
  };
};

/**
 * Generate structured data for AboutPage
 */
export const generateAboutPageStructuredData = (options: {
  title: string;
  description: string;
  url: string;
  organizationName?: string;
  foundingDate?: string;
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: options.title,
    description: options.description,
    url: options.url,
    inLanguage: defaults.locale,
    ...(options.organizationName && {
      mainEntity: {
        '@type': 'Organization',
        name: options.organizationName,
        ...(options.foundingDate && {
          foundingDate: options.foundingDate,
        }),
      },
    }),
  };
};

/**
 * Generate structured data for ImageGallery
 */
export const generateImageGalleryStructuredData = (options: {
  name: string;
  description: string;
  url: string;
  images: Array<{ url: string; name?: string; description?: string }>;
}) => {
  const baseUrl = getBaseUrl();
  const defaults = getDefaultSEO();

  return {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: options.name,
    description: options.description,
    url: options.url,
    inLanguage: defaults.locale,
    image: options.images.map((img) => ({
      '@type': 'ImageObject',
      url: img.url.startsWith('http') ? img.url : `${baseUrl}${img.url}`,
      ...(img.name && { name: img.name }),
      ...(img.description && { description: img.description }),
    })),
  };
};

/**
 * Generate structured data for WebSite with SearchAction
 * Only includes SearchAction if searchActionUrl is provided
 */
export const generateWebSiteStructuredData = (options: {
  name: string;
  url: string;
  searchActionUrl?: string;
  inLanguage?: string;
}) => {
  const baseUrl = getBaseUrl();
  
  // Ensure searchActionUrl is absolute if provided
  let absoluteSearchActionUrl: string | undefined;
  if (options.searchActionUrl) {
    if (options.searchActionUrl.startsWith('http://') || options.searchActionUrl.startsWith('https://')) {
      absoluteSearchActionUrl = options.searchActionUrl;
    } else {
      // Make relative URL absolute
      const path = options.searchActionUrl.startsWith('/') ? options.searchActionUrl : `/${options.searchActionUrl}`;
      absoluteSearchActionUrl = `${baseUrl}${path}`;
    }
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: options.name,
    url: options.url,
    ...(options.inLanguage && {
      inLanguage: options.inLanguage,
    }),
    ...(absoluteSearchActionUrl && {
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: absoluteSearchActionUrl,
        },
        'query-input': 'required name=search_term_string',
      },
    }),
  };
};


























